var _effectorFileName = "/src/shared/components/promo-modal/promo-modal.tsx";
import * as React from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { Button } from '@/shared/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { eventFB } from '@/shared/lib/analytics/facebook-pixel';
import { eventGA } from '@/shared/lib/analytics/google';
import { eventYM } from '@/shared/lib/analytics/yandex';
import { cn } from '@/shared/lib/cn';
import { AD_PROMO_QUERY, dismissPromo, hasTrackedPromoView, isPromoDismissed, markPromoViewTracked, shouldSchedulePromo } from './lib';
import { AD_PROMO_SHOW_DELAY_MS, formatPromoCountdown, getPromoRemainingMs, isPromoDeadlinePassed } from './timer';
interface PromoModalProps {
  onClaim: () => void;
}
export const PromoModal = (props: PromoModalProps) => {
  const {
    onClaim
  } = props;
  const {
    t
  } = useTranslation('common');
  const router = useRouter();
  const [open, setOpen] = React.useState(false);
  const [remainingMs, setRemainingMs] = React.useState(() => getPromoRemainingMs());
  const deadlinePassed = isPromoDeadlinePassed();
  const trackPromoView = React.useEffectEvent(() => {
    if (hasTrackedPromoView()) return;
    markPromoViewTracked();
    eventGA('ad_promo_view');
    eventYM('ad_promo_view');
    eventFB('ViewContent', {
      content_name: 'ad_promo'
    });
  });
  React.useEffect(() => {
    const queryValue = router.query[AD_PROMO_QUERY];
    if (!shouldSchedulePromo(queryValue, isPromoDismissed())) return;
    trackPromoView();
    const timeoutId = window.setTimeout(() => {
      if (isPromoDismissed()) return;
      setOpen(true);
    }, AD_PROMO_SHOW_DELAY_MS);
    return () => window.clearTimeout(timeoutId);
  }, [router.query, trackPromoView]);
  React.useEffect(() => {
    if (!open || deadlinePassed) return;
    setRemainingMs(getPromoRemainingMs());
    const intervalId = window.setInterval(() => {
      setRemainingMs(getPromoRemainingMs());
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [open, deadlinePassed]);
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      dismissPromo();
    }
    setOpen(nextOpen);
  };
  const handleClaim = () => {
    dismissPromo();
    setOpen(false);
    onClaim();
  };
  return <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent overlayClassName="bg-black/20 dark:bg-white/10" className={cn('max-w-[min(100%-2rem,28rem)] gap-6 rounded-3xl border-border bg-background p-6 shadow-lg sm:max-w-md', 'max-md:bottom-4 max-md:left-1/2 max-md:top-auto max-md:translate-x-[-50%] max-md:translate-y-0', 'max-md:data-[state=closed]:slide-out-to-bottom-4 max-md:data-[state=open]:slide-in-from-bottom-4', 'md:left-[50%] md:top-[50%] md:translate-x-[-50%] md:translate-y-[-50%]')}>
        <DialogHeader className="space-y-3 text-left">
          <DialogTitle className="text-center text-3xl font-bold italic leading-tight tracking-tight sm:text-4xl">
            {t('adPromo.title')}
          </DialogTitle>
          <DialogDescription className="mx-auto max-w-xs text-center text-base text-muted-foreground">
            {t('adPromo.subtitle')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
          {!deadlinePassed && <div className="flex items-center justify-center rounded-xl border border-foreground px-3 py-1 font-mono text-sm font-semibold tabular-nums sm:text-base" data-testid="ad-promo-timer" aria-live="polite">
              {formatPromoCountdown(remainingMs)}
            </div>}

          <Button variant="cta" className="!h-10 flex-1 rounded-xl text-base font-semibold" onClick={handleClaim}>
            {t('adPromo.cta')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>;
};